import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CreditCard, Building2, Smartphone, Plus, Trash2, DollarSign, 
  Eye, EyeOff, Save
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AdminPaymentSettings = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [paymentSettings, setPaymentSettings] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [upiSettings, setUpiSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});
  
  // Forms
  const [paymentForm, setPaymentForm] = useState({
    gateway_name: "Stripe",
    api_key: "",
    webhook_secret: "",
    is_active: true
  });
  
  const [bankForm, setBankForm] = useState({
    account_holder_name: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    branch: "",
    account_type: "savings",
    is_active: true
  });
  
  const [upiForm, setUpiForm] = useState({
    upi_id: "",
    name: "",
    is_active: true
  });

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "school_admin") {
      toast.error("Access denied: Admin only");
      navigate("/");
      return;
    }
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [paymentsRes, banksRes, upiRes] = await Promise.all([
        axios.get(`${API}/admin/payment-settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bank-accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/upi-settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setPaymentSettings(paymentsRes.data.settings);
      setBankAccounts(banksRes.data.accounts);
      setUpiSettings(upiRes.data.settings);
    } catch (error) {
      toast.error("Failed to load payment settings");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentGateway = async () => {
    try {
      await axios.post(`${API}/admin/payment-settings`, paymentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment gateway added");
      setShowPaymentModal(false);
      setPaymentForm({ gateway_name: "Stripe", api_key: "", webhook_secret: "", is_active: true });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add payment gateway");
    }
  };

  const handleDeletePaymentGateway = async (id) => {
    if (!confirm("Delete this payment gateway?")) return;
    try {
      await axios.delete(`${API}/admin/payment-settings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment gateway deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete payment gateway");
    }
  };

  const handleAddBankAccount = async () => {
    try {
      await axios.post(`${API}/admin/bank-accounts`, bankForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bank account added");
      setShowBankModal(false);
      setBankForm({
        account_holder_name: "",
        bank_name: "",
        account_number: "",
        ifsc_code: "",
        branch: "",
        account_type: "savings",
        is_active: true
      });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add bank account");
    }
  };

  const handleDeleteBankAccount = async (id) => {
    if (!confirm("Delete this bank account?")) return;
    try {
      await axios.delete(`${API}/admin/bank-accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bank account deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete bank account");
    }
  };

  const handleAddUpi = async () => {
    try {
      await axios.post(`${API}/admin/upi-settings`, upiForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("UPI ID added");
      setShowUpiModal(false);
      setUpiForm({ upi_id: "", name: "", is_active: true });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to add UPI ID");
    }
  };

  const handleDeleteUpi = async (id) => {
    if (!confirm("Delete this UPI ID?")) return;
    try {
      await axios.delete(`${API}/admin/upi-settings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("UPI ID deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete UPI ID");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Payment Settings</h1>
          <p className="text-gray-600">Manage payment gateways, bank accounts, and UPI IDs</p>
        </div>

        <Tabs defaultValue="gateways" className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="gateways">Payment Gateways</TabsTrigger>
            <TabsTrigger value="banks">Bank Accounts</TabsTrigger>
            <TabsTrigger value="upi">UPI IDs</TabsTrigger>
          </TabsList>

          {/* Payment Gateways */}
          <TabsContent value="gateways">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Payment Gateways</h2>
              <Button onClick={() => setShowPaymentModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Gateway
              </Button>
            </div>

            {paymentSettings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-4">No payment gateways configured</p>
                  <Button onClick={() => setShowPaymentModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Gateway
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paymentSettings.map((setting) => (
                  <Card key={setting.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{setting.gateway_name}</CardTitle>
                        <Badge variant={setting.is_active ? "default" : "secondary"}>
                          {setting.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">API Key:</span>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {showApiKey[setting.id] ? setting.api_key : "••••••••••••"}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowApiKey({...showApiKey, [setting.id]: !showApiKey[setting.id]})}
                            >
                              {showApiKey[setting.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          <strong>Created:</strong> {new Date(setting.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeletePaymentGateway(setting.id)}
                        className="w-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Bank Accounts */}
          <TabsContent value="banks">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Bank Accounts</h2>
              <Button onClick={() => setShowBankModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Bank Account
              </Button>
            </div>

            {bankAccounts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-4">No bank accounts added</p>
                  <Button onClick={() => setShowBankModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Bank Account
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bankAccounts.map((account) => (
                  <Card key={account.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{account.bank_name}</CardTitle>
                        <Badge variant={account.is_active ? "default" : "secondary"}>
                          {account.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 mb-4 text-sm">
                        <div><strong>Holder:</strong> {account.account_holder_name}</div>
                        <div><strong>Account:</strong> ••••{account.account_number.slice(-4)}</div>
                        <div><strong>IFSC:</strong> {account.ifsc_code}</div>
                        <div><strong>Type:</strong> {account.account_type.toUpperCase()}</div>
                        {account.branch && <div><strong>Branch:</strong> {account.branch}</div>}
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteBankAccount(account.id)}
                        className="w-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* UPI IDs */}
          <TabsContent value="upi">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">UPI IDs</h2>
              <Button onClick={() => setShowUpiModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add UPI ID
              </Button>
            </div>

            {upiSettings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Smartphone className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-4">No UPI IDs configured</p>
                  <Button onClick={() => setShowUpiModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add UPI ID
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {upiSettings.map((upi) => (
                  <Card key={upi.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{upi.name}</CardTitle>
                        <Badge variant={upi.is_active ? "default" : "secondary"}>
                          {upi.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4">
                        <code className="text-sm bg-gray-100 px-3 py-2 rounded block">
                          {upi.upi_id}
                        </code>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteUpi(upi.id)}
                        className="w-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Payment Gateway Modal */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Payment Gateway</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Gateway Name</Label>
                <Select 
                  value={paymentForm.gateway_name}
                  onValueChange={(val) => setPaymentForm({...paymentForm, gateway_name: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Stripe">Stripe</SelectItem>
                    <SelectItem value="PayPal">PayPal</SelectItem>
                    <SelectItem value="Razorpay">Razorpay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>API Key *</Label>
                <Input
                  type="password"
                  value={paymentForm.api_key}
                  onChange={(e) => setPaymentForm({...paymentForm, api_key: e.target.value})}
                  placeholder="sk_live_..."
                />
              </div>
              <div>
                <Label>Webhook Secret (Optional)</Label>
                <Input
                  type="password"
                  value={paymentForm.webhook_secret}
                  onChange={(e) => setPaymentForm({...paymentForm, webhook_secret: e.target.value})}
                  placeholder="whsec_..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
              <Button onClick={handleAddPaymentGateway}>
                <Save className="w-4 h-4 mr-2" />
                Save Gateway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bank Account Modal */}
        <Dialog open={showBankModal} onOpenChange={setShowBankModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Bank Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Account Holder Name *</Label>
                <Input
                  value={bankForm.account_holder_name}
                  onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Bank Name *</Label>
                <Input
                  value={bankForm.bank_name}
                  onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={bankForm.account_number}
                  onChange={(e) => setBankForm({...bankForm, account_number: e.target.value})}
                />
              </div>
              <div>
                <Label>IFSC Code *</Label>
                <Input
                  value={bankForm.ifsc_code}
                  onChange={(e) => setBankForm({...bankForm, ifsc_code: e.target.value})}
                />
              </div>
              <div>
                <Label>Branch</Label>
                <Input
                  value={bankForm.branch}
                  onChange={(e) => setBankForm({...bankForm, branch: e.target.value})}
                />
              </div>
              <div>
                <Label>Account Type</Label>
                <Select 
                  value={bankForm.account_type}
                  onValueChange={(val) => setBankForm({...bankForm, account_type: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">Savings</SelectItem>
                    <SelectItem value="current">Current</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBankModal(false)}>Cancel</Button>
              <Button onClick={handleAddBankAccount}>
                <Save className="w-4 h-4 mr-2" />
                Save Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* UPI Modal */}
        <Dialog open={showUpiModal} onOpenChange={setShowUpiModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add UPI ID</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>UPI ID *</Label>
                <Input
                  value={upiForm.upi_id}
                  onChange={(e) => setUpiForm({...upiForm, upi_id: e.target.value})}
                  placeholder="username@bank"
                />
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input
                  value={upiForm.name}
                  onChange={(e) => setUpiForm({...upiForm, name: e.target.value})}
                  placeholder="Your Business Name"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpiModal(false)}>Cancel</Button>
              <Button onClick={handleAddUpi}>
                <Save className="w-4 h-4 mr-2" />
                Save UPI ID
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default AdminPaymentSettings;
